import React, { Dispatch, SetStateAction, useCallback, useMemo } from 'react'
import cn from 'classnames'
import { Dropdown } from '@corratech/pylot-dropdown'
import { ChevronDown, ChevronUp } from 'react-feather'
import { useTranslation } from 'next-i18next/pages'
import type {
    ProductAttributeSortInput,
    SortFields
} from '@pylot-data/fwrdschema'
import { SortEnum } from '@pylot-data/enums/SortEnum'
import { Button } from '@corratech/pylot-ui'
import s from './SortDropdown.module.scss'

type SortProps = {
    sortFields: SortFields
    applySort: Dispatch<SetStateAction<ProductAttributeSortInput>>
    activeLabel: string
    setActiveLabel: Dispatch<SetStateAction<string>>
    appliedSort?: ProductAttributeSortInput | Record<string, never>
}

export const SortDropdown = (props: SortProps): JSX.Element => {
    const [t] = useTranslation('common')
    const {
        sortFields: { default: defaultSort, options },
        applySort,
        activeLabel,
        setActiveLabel
    } = props

    const getSortItem = useCallback(
        (value: string, label: string, direction?: SortEnum) => (
            <li key={`${value}${direction}`} className={cn(s['sort-item'])}>
                <Button
                    onClick={() => {
                        applySort({ [value]: direction ?? SortEnum.Asc })
                        setActiveLabel(label)
                    }}
                    className={cn(s['sort-item-anchor'])}
                    variant="link"
                >
                    {t(label)}
                </Button>
            </li>
        ),
        [applySort, t]
    )

    const sortOptionItems = useMemo(
        () =>
            options &&
            options.map((item) => {
                if (!item || !item.value) {
                    return null
                }
                if (activeLabel === '' && defaultSort === item.value) {
                    setActiveLabel(String(item.label))
                }
                return item.value !== 'price' ? (
                    getSortItem(item.value, String(item.label))
                ) : (
                    <>
                        {getSortItem(item.value, 'Price High', SortEnum.Desc)}
                        {getSortItem(item.value, 'Price Low', SortEnum.Asc)}
                    </>
                )
            }),
        [activeLabel, defaultSort, getSortItem, options]
    )

    const dropdownLabel = (isOpen: boolean) => (
        <button className={s['sort-label']}>
            <span className={s['plp-btn-txt']}>{t('Sort by')}:</span>
            <span className={s['active-sort-label']}>{activeLabel}</span>
            {isOpen ? <ChevronDown /> : <ChevronUp />}
        </button>
    )

    return (
        <Dropdown
            className={s['sort']}
            openedDisplay={dropdownLabel(false)}
            closedDisplay={dropdownLabel(true)}
        >
            <ul>{sortOptionItems}</ul>
        </Dropdown>
    )
}
